import { useCallback, useMemo, useState } from "react";
import { styled } from "styled-components";
import { useDropzone } from "react-dropzone-esm";
import { uploadFile } from "@ludens-reklame/rubics-v3-sdk/file";
import { File as RubicsFile } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import {
  destroy,
  upload as uploadIcon,
} from "../../icons/index.js";

const Wrapper = styled.div`
  border: 1px dashed var(--swatch2);
  border-radius: 5px;
  padding: 0 ${(props) => props.theme.spacing.xs};
  margin-top: ${(props) => props.theme.spacing.xs};
  background-color: #fff;
  color: var(--swatch2);
  outline: none;
  cursor: pointer;

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: 0 ${(props) => props.theme.spacing.xs};
    margin-top: ${(props) => props.theme.spacing.xs};
  }
`;

const Upload = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing.xs} 0;

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) => props.theme.spacing.s} 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${(props) => props.theme.spacing.xs};
  font-weight: 600;
`;

const Icon = styled.div`
  margin-right: ${(props) => props.theme.spacing.xs};
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 38px;
    height: 38px;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    margin-right: ${(props) => props.theme.spacing.xs};
  }
`;

const Text = styled.p`
  margin: 0;
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing.xs} 0;
  flex-wrap: wrap;

  img {
    display: block;
    height: 50px;
    margin-right: ${(props) => props.theme.spacing.xs};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    img {
      margin-right: ${(props) => props.theme.spacing.xs};
    }
  }
`;

const Loading = styled.p`
  margin: ${(props) => props.theme.spacing.xs} 0 0;
  width: 100%;
  color: var(--swatch1);
  text-align: center;

  @media (min-width: ${mobileBreakPointCSS}) {
    margin-top: 0;
    margin-left: auto;
    width: auto;
    text-align: left;
  }
`;

const Delete = styled.a`
  display: block;
  text-decoration: none;
  margin-left: auto;
  margin-top: ${(props) => props.theme.spacing.xs};
  color: var(--error);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  svg {
    width: 20px;
    height: 20px;
    margin-left: ${(props) => props.theme.spacing.xs};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    width: auto;
    margin-left: auto;
    justify-content: flex-start;
    margin-top: 0;

    svg {
      margin-left: ${(props) => props.theme.spacing.xs};
    }
  }
`;

const Error = styled.div`
  color: var(--error);
`;

interface Props {
  siteName: string;
  id: string;
  onUpload: (image: RubicsFile) => any;
  onDelete: () => any;
  defaultImage?: RubicsFile;
  required?: boolean;
  name?: string;
  label?: string;
}

export const ImageUploader: React.FunctionComponent<Props> = ({
  id,
  defaultImage,
  onUpload,
  onDelete,
  required,
  label,
  name,
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [files, setFiles] = useState([] as File[]);
  const [isInvalid, setIsInvalid] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
        upload(acceptedFiles[0]);
      }
    },
    [onUpload]
  );

  //@ts-ignore
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  });

  const image: RubicsFile | undefined = useMemo(() => {
    if (files.length > 0) {
      const file = files[0];

      return {
        _id: "preview",
        url: URL.createObjectURL(file),
        name: file.name,
        mimeType: "",
        isImage: true,
        size: 0,
        downloads: 0,
        uploaded: "",
      };
    } else if (defaultImage && !deleted) {
      return defaultImage;
    }

    return undefined;
  }, [files]);

  async function upload(image: File) {
    setUploading(true);

    const file = await uploadFile(image);

    setUploading(false);

    if (file && "url" in file) {
      onUpload(file);
      setIsInvalid(false);
    }
  }

  function handleDelete() {
    setFiles([]);
    setDeleted(true);
    onDelete();
  }

  return (
    <>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Wrapper {...getRootProps()}>
        <input
          id={id}
          name={name}
          {...getInputProps()}
          required={required && !image}
          onInvalid={() => {
            setIsInvalid(true);
          }}
        />
        {image ? (
          <Preview>
            <img src={image.url} alt="Forhåndsvisning av opplastet bilde" />
            <Text>{image.name}</Text>
            {uploading ? (
              <Loading>Laster opp…</Loading>
            ) : (
              <Delete
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete();
                }}
              >
                Slett {destroy}
              </Delete>
            )}
          </Preview>
        ) : (
          <Upload>
            <Icon>{uploadIcon}</Icon>
            <Text>
              {isDragActive
                ? "Slipp filene her…"
                : "Dra og slipp filer her eller klikk…"}
            </Text>
          </Upload>
        )}
      </Wrapper>
      {isInvalid && (
        <Error>Vi trenger denne filen fra deg for å forsette.</Error>
      )}
    </>
  );
};
